function calculateVerticalTimelineGeometry(
  trackTop,
  firstDotTop,
  firstDotHeight,
  lastDotTop,
  lastDotHeight
) {
  const top = firstDotTop - trackTop + firstDotHeight / 2;
  const lastCenter = lastDotTop - trackTop + lastDotHeight / 2;

  return {
    top,
    height: Math.max(0, lastCenter - top)
  };
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { calculateVerticalTimelineGeometry };
}

(() => {
  if (typeof document === 'undefined') return;

  const DEFAULT_TIME_ZONE = 'America/Los_Angeles';
  const MONTHS = [
    'Jan.',
    'Feb.',
    'Mar.',
    'Apr.',
    'May',
    'Jun.',
    'Jul.',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.'
  ];

  function getDateParts(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      timeZoneName: 'short'
    }).formatToParts(date);

    return {
      year: Number(parts.find((part) => part.type === 'year').value),
      month: Number(parts.find((part) => part.type === 'month').value),
      day: Number(parts.find((part) => part.type === 'day').value),
      timeZoneName: parts.find((part) => part.type === 'timeZoneName').value
    };
  }

  function isSameDate(firstDate, secondDate, timeZone) {
    const first = getDateParts(firstDate, timeZone);
    const second = getDateParts(secondDate, timeZone);

    return first.year === second.year &&
      first.month === second.month &&
      first.day === second.day;
  }

  function formatTimelineDate(date, timeZone) {
    const parts = getDateParts(date, timeZone);
    return `${MONTHS[parts.month - 1]} ${parts.day} ${parts.timeZoneName}`;
  }

  function getTimeZoneOffset(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(date);

    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const zonedTimeAsUtc = Date.UTC(
      Number(values.year),
      Number(values.month) - 1,
      Number(values.day),
      Number(values.hour),
      Number(values.minute),
      Number(values.second)
    );

    return zonedTimeAsUtc - date.getTime();
  }

  function parseDateTime(value, timeZone) {
    const normalizedValue = value.trim();

    if (/([zZ]|[+-]\d{2}:\d{2})$/.test(normalizedValue)) {
      return new Date(normalizedValue);
    }

    const match = normalizedValue.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?$/
    );

    if (!match) return new Date(NaN);

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hour = Number(match[4] || 0);
    const minute = Number(match[5] || 0);
    const requestedTimeAsUtc = Date.UTC(year, month - 1, day, hour, minute);
    const initialDate = new Date(requestedTimeAsUtc);
    const initialOffset = getTimeZoneOffset(initialDate, timeZone);
    const adjustedDate = new Date(requestedTimeAsUtc - initialOffset);
    const adjustedOffset = getTimeZoneOffset(adjustedDate, timeZone);

    return new Date(requestedTimeAsUtc - adjustedOffset);
  }

  function getTimelineTextBounds(item, selector) {
    const textElements = item.querySelectorAll(selector);
    const rects = [];

    textElements.forEach((element) => {
      if (!element.textContent.trim()) return;

      const range = document.createRange();
      range.selectNodeContents(element);
      rects.push(...Array.from(range.getClientRects()).filter((rect) => rect.width > 0));
    });

    if (!rects.length) return null;

    return {
      left: Math.min(...rects.map((rect) => rect.left)),
      right: Math.max(...rects.map((rect) => rect.right))
    };
  }

  function clamp(value, minimum, maximum) {
    if (maximum < minimum) return (minimum + maximum) / 2;
    return Math.min(maximum, Math.max(minimum, value));
  }

  function getDesktopCurrentPosition(timeline, items, desiredPosition) {
    if (!items[1] || items[1].classList.contains('is-hidden-same-day')) {
      return desiredPosition;
    }

    const trackRect = timeline.getBoundingClientRect();
    const startBody = getTimelineTextBounds(
      items[0],
      '.e1r-timeline__label, .e1r-timeline__detail'
    );
    const currentBody = getTimelineTextBounds(
      items[1],
      '.e1r-timeline__label, .e1r-timeline__detail'
    );
    const releaseBody = getTimelineTextBounds(
      items[items.length - 1],
      '.e1r-timeline__label, .e1r-timeline__detail'
    );
    const startDate = getTimelineTextBounds(items[0], '.e1r-timeline__date');
    const currentDate = getTimelineTextBounds(items[1], '.e1r-timeline__date');
    const releaseDate = getTimelineTextBounds(
      items[items.length - 1],
      '.e1r-timeline__date'
    );

    let minimum = 17;
    let maximum = trackRect.width - 17;

    if (startBody && currentBody) {
      minimum = Math.max(
        minimum,
        startBody.right - trackRect.left + 24 + (currentBody.right - currentBody.left) / 2
      );
    }

    if (releaseBody && currentBody) {
      maximum = Math.min(
        maximum,
        releaseBody.left - trackRect.left - 24 - (currentBody.right - currentBody.left) / 2
      );
    }

    if (startDate && currentDate) {
      minimum = Math.max(
        minimum,
        startDate.right - trackRect.left + 14 + (currentDate.right - currentDate.left) / 2
      );
    }

    if (releaseDate && currentDate) {
      maximum = Math.min(
        maximum,
        releaseDate.left - trackRect.left - 14 - (currentDate.right - currentDate.left) / 2
      );
    }

    return clamp(desiredPosition, minimum, maximum);
  }

  function setMobileTimelinePositions(timeline, items, progressRatio) {
    if (items.length < 3) return;

    const startBody = items[0].querySelector('.e1r-timeline__body');
    const currentBody = items[1].querySelector('.e1r-timeline__body');
    const releaseBody = items[items.length - 1].querySelector('.e1r-timeline__body');
    const startHeight = startBody ? startBody.offsetHeight : 32;
    const currentHeight = currentBody ? currentBody.offsetHeight : 48;
    const releaseHeight = releaseBody ? releaseBody.offsetHeight : 32;
    const verticalGap = 28;
    const releaseTop = Math.max(
      270,
      startHeight + currentHeight + releaseHeight + verticalGap * 2
    );
    const minimum = startHeight + verticalGap;
    const maximum = releaseTop - currentHeight - verticalGap;
    const currentTop = clamp(releaseTop * progressRatio, minimum, maximum);

    timeline.style.setProperty('--mobile-current-top', `${currentTop}px`);
    timeline.style.setProperty('--mobile-release-top', `${releaseTop}px`);
    timeline.style.setProperty(
      '--mobile-track-bottom-space',
      `${Math.max(52, releaseHeight + 18)}px`
    );
  }

  function updateTimeline(timeline) {
    const timeZone = timeline.dataset.timeZone || DEFAULT_TIME_ZONE;
    const start = parseDateTime(timeline.dataset.startTime, timeZone);
    const release = parseDateTime(timeline.dataset.releaseTime, timeZone);

    if (Number.isNaN(start.getTime()) || Number.isNaN(release.getTime())) return;
    if (release.getTime() <= start.getTime()) return;

    const now = new Date();
    const items = Array.from(timeline.querySelectorAll('.e1r-timeline__item'));
    const hideCurrentNode =
      now.getTime() < start.getTime() || isSameDate(start, now, timeZone);

    if (items[1]) {
      items[1].classList.toggle('is-hidden-same-day', hideCurrentNode);
    }

    const elapsed = now.getTime() - start.getTime();
    const duration = release.getTime() - start.getTime();
    const progress = Math.min(100, Math.max(0, (elapsed / duration) * 100));
    const progressRatio = progress / 100;
    const visualProgressRatio = hideCurrentNode ? 0 : progressRatio;
    const startDate = timeline.querySelector('[data-date-role="start"]');
    const currentDate = timeline.querySelector('[data-date-role="current"]');
    const releaseDate = timeline.querySelector('[data-date-role="release"]');

    if (startDate) startDate.textContent = formatTimelineDate(start, timeZone);
    if (currentDate) currentDate.textContent = formatTimelineDate(now, timeZone);
    if (releaseDate) releaseDate.textContent = formatTimelineDate(release, timeZone);

    if (items[2]) {
      items[2].classList.toggle('is-released', now.getTime() >= release.getTime());
    }

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const dots = timeline.querySelectorAll('.e1r-timeline__dot');
    const trackRect = timeline.getBoundingClientRect();
    const firstDotRect = dots[0] ? dots[0].getBoundingClientRect() : null;
    const lastDotRect = dots[dots.length - 1]
      ? dots[dots.length - 1].getBoundingClientRect()
      : null;
    const startCenter = firstDotRect
      ? firstDotRect.left - trackRect.left + firstDotRect.width / 2
      : 20;
    const releaseCenter = lastDotRect
      ? lastDotRect.left - trackRect.left + lastDotRect.width / 2
      : timeline.clientWidth - 20;
    const desiredPosition = startCenter +
      (releaseCenter - startCenter) * visualProgressRatio;

    if (isMobile) {
      setMobileTimelinePositions(timeline, items, visualProgressRatio);
    }

    const progressPosition = isMobile
      ? desiredPosition
      : getDesktopCurrentPosition(timeline, items, desiredPosition);
    const progressWidth = Math.max(0, progressPosition - 31);

    timeline.style.setProperty(
      '--progress',
      `${(visualProgressRatio * 100).toFixed(2)}%`
    );
    timeline.style.setProperty('--progress-position', `${progressPosition}px`);
    timeline.style.setProperty('--progress-width', `${progressWidth}px`);
    if (dots.length >= 2) {
      const updatedTrackRect = timeline.getBoundingClientRect();
      const updatedFirstDotRect = dots[0].getBoundingClientRect();
      const updatedLastDotRect = dots[dots.length - 1].getBoundingClientRect();
      const verticalGeometry = calculateVerticalTimelineGeometry(
        updatedTrackRect.top,
        updatedFirstDotRect.top,
        updatedFirstDotRect.height,
        updatedLastDotRect.top,
        updatedLastDotRect.height
      );

      timeline.style.setProperty('--vertical-line-top', `${verticalGeometry.top}px`);
      timeline.style.setProperty('--vertical-line-height', `${verticalGeometry.height}px`);

      let mobileProgressHeight = 0;

      if (now.getTime() >= release.getTime()) {
        mobileProgressHeight = verticalGeometry.height;
      } else if (now.getTime() >= start.getTime() && dots[1] && !hideCurrentNode) {
        const currentDotRect = dots[1].getBoundingClientRect();
        const currentDotCenter =
          currentDotRect.top - updatedTrackRect.top + currentDotRect.height / 2;
        mobileProgressHeight = Math.max(0, currentDotCenter - verticalGeometry.top);
      }

      timeline.style.setProperty('--progress-height', `${mobileProgressHeight}px`);
    }

  }

  function initializeTimelines(root = document) {
    root.querySelectorAll('[data-e1r-timeline]').forEach(updateTimeline);
  }

  document.addEventListener('DOMContentLoaded', () => initializeTimelines());
  document.addEventListener('shopify:section:load', (event) => initializeTimelines(event.target));
  window.addEventListener('resize', () => initializeTimelines());
  window.setInterval(() => initializeTimelines(), 60 * 1000);
})();
